import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Button,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";

const SportsSection = () => {
  const [mounted, setMounted] = React.useState(false);
  const [sportsStories, setSportsStories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (!mounted) return;
    
    const fetchSportsStories = async () => {
      try {
        const response = await fetch(
          "https://ethioinsider-backend.onrender.com/api/articles/sports"
        );
        if (!response.ok) {
          throw new Error("Failed to fetch sports stories");
        }
        const result = await response.json();
        if (result.data && Array.isArray(result.data)) {
          setSportsStories(result.data.slice(0, 3));
        } else {
          throw new Error("Unexpected API response format");
        }
      } catch (err) {
        setError(err.message);
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSportsStories();
  }, [mounted]);

  if (!mounted) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography
        variant="body1"
        color="error"
        sx={{ textAlign: "center", py: 4 }}
      >
        Error loading sports stories: {error}
      </Typography>
    );
  }

  if (sportsStories.length === 0) {
    return (
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ textAlign: "center", py: 4 }}
      >
        No sports stories available at the moment.
      </Typography>
    );
  }

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid xs={12} md={8}>
          <Card
            square
            elevation={1}
            sx={{ height: "100%", minHeight: "400px" }}
          >
            <CardActionArea
              href={`/article/${sportsStories[0]?._id}`}
              sx={{ height: "100%" }}
            >
              <CardMedia
                component="img"
                height="240"
                image={
                  sportsStories[0]?.imageUrl ||
                  "https://via.placeholder.com/800x450?text=Sports"
                }
                alt={sportsStories[0]?.title}
                sx={{ objectFit: "cover", width: "100%" }}
                onError={(e) => {
                  e.target.src =
                    "https://via.placeholder.com/800x450?text=Sports";
                }}
              />
              <CardContent>
                <Chip
                  label="Sports"
                  color="primary"
                  size="small"
                  sx={{ mb: 1 }}
                />
                <Typography variant="h6" maxWidth="550px">
                  {sportsStories[0]?.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  paragraph
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    maxWidth: "500px",
                  }}
                >
                  {sportsStories[0]?.excerpt || "No excerpt available"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(sportsStories[0]?.createdAt).toLocaleDateString()}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid xs={12} md={4}>
          <Stack spacing={2} sx={{ height: "100%" }}>
            {sportsStories.slice(1, 3).map((story) => (
              <Card
                key={`sports-${story._id}`}
                square
                elevation={1}
                sx={{ height: "calc(50% - 8px)" }}
              >
                <CardActionArea
                  component={Link}
                  to={`/articles/${story._id}`}
                  sx={{ height: "100%" }}
                >
                  <CardMedia
                    component="img"
                    height="140"
                    image={
                      story?.imageUrl ||
                      "https://via.placeholder.com/400x250?text=Sports"
                    }
                    alt={story?.title}
                    sx={{
                      objectFit: "cover",
                      width: "100%",
                      height: "140px",
                    }}
                    onError={(e) => {
                      e.target.src =
                        "https://via.placeholder.com/400x250?text=Sports";
                    }}
                  />
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: "3em",
                      }}
                    >
                      {story?.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(story?.createdAt).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        </Grid>
      </Grid>

      {sportsStories.length >= 3 && (
        <Box textAlign="center" sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            component={Link}
            to="/sports-news"
          >
            View All Sports News
          </Button>
        </Box>
      )}
    </>
  );
};

export default SportsSection;
